import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, Form, InputGroup, Placeholder } from 'react-bootstrap'
import { FaBirthdayCake, FaSearch, FaWhatsapp } from 'react-icons/fa'
import { IoMailOutline } from 'react-icons/io5'
import { useNavigate } from 'react-router-dom'
import { SweetAlert } from '../../components/sweetalert'
import { URL } from '../../config/url'
import { useAuth } from '../../context/auth'
import Birthdays from '../../services/Birthdays'
import { BIRTHDAY_DISPLAYS, DEFAULT_BIRTHDAY_DISPLAY, DEFAULT_WHATSAPP_MESSAGE, MONTHS } from '../../utilities/birthdays'
import BirthdayCard from './BirthdayCard'
import BirthdayGreetingsModal from './BirthdayGreetingsModal'
import BirthdayWhatsappModal from './BirthdayWhatsappModal'
import './style.css'

const SEARCH_MIN_LENGTH = 2

const BirthdaysPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  const [month, setMonth] = useState(new Date().getMonth() + 1)
  const [displayByMonth, setDisplayByMonth] = useState({}) // "Exibir" de cada mês: a escolha de um mês não vale para os outros
  const [totals, setTotals] = useState(null)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [items, setItems] = useState([])
  const [paging, setPaging] = useState({ page: 0, total_pages: 1, total_records: 0 })
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [showGreetings, setShowGreetings] = useState(false)
  const [showWhatsapp, setShowWhatsapp] = useState(false)
  const [whatsappMessage, setWhatsappMessage] = useState(DEFAULT_WHATSAPP_MESSAGE)

  const requestRef = useRef(0)
  const loaderRef = useRef(null)

  useEffect(() => {
    Birthdays.listMonths()
      .then(({ data }) => setTotals(data))
      .catch((error) => SweetAlert.error(error?.response?.data?.message || error?.message, 'Aniversariantes'))

    // Mensagem do botão Parabenizar; se não carregar, fica a padrão
    Birthdays.findConfig()
      .then(({ data }) => setWhatsappMessage(data.whatsapp_message || DEFAULT_WHATSAPP_MESSAGE))
      .catch(() => { })
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400)
    return () => clearTimeout(timer)
  }, [search])

  const nome = (debouncedSearch.length >= SEARCH_MIN_LENGTH) ? debouncedSearch : ''
  const display = displayByMonth[month] || DEFAULT_BIRTHDAY_DISPLAY

  // Página 1 troca a lista; as próximas acrescentam. Resposta de um mês/busca/"Exibir" antigos é descartada
  const loadPage = useCallback((page) => {
    const requestId = ++requestRef.current
    if (page === 1) setLoading(true)
    else setLoadingMore(true)

    Birthdays.listAll(month, page, nome, display)
      .then(({ data }) => {
        if (requestId !== requestRef.current) return
        setItems((current) => (page === 1) ? data.data : [...current, ...data.data])
        setPaging({ page, total_pages: data.paging.total_pages, total_records: data.paging.total_records })
      })
      .catch((error) => {
        if (requestId !== requestRef.current) return
        SweetAlert.error(error?.response?.data?.message || error?.message || 'Não foi possível carregar os aniversariantes.', 'Aniversariantes')
      })
      .finally(() => {
        if (requestId !== requestRef.current) return
        setLoading(false)
        setLoadingMore(false)
      })
  }, [month, nome, display])

  useEffect(() => {
    setItems([])
    setPaging({ page: 0, total_pages: 1, total_records: 0 })
    loadPage(1)
  }, [loadPage])

  const hasNextPage = paging.page > 0 && paging.page < paging.total_pages

  // Carrega a próxima página quando o fim da lista aparece na tela
  useEffect(() => {
    const element = loaderRef.current
    if (!element || !hasNextPage || loading || loadingMore) return

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadPage(paging.page + 1)
    }, { rootMargin: '200px' })

    observer.observe(element)
    return () => observer.disconnect()
  }, [hasNextPage, loading, loadingMore, loadPage, paging.page])

  function handleEdit(id) {
    navigate(URL.pacientes_cadastro + '/' + id)
  }

  return (
    <div className='pb-4' style={{ '--birthdays-primary': 'var(--app-brand)' }}>
      <div className='d-flex justify-content-between align-items-center flex-wrap gap-3 px-3 pt-3'>
        <div className='d-flex align-items-center gap-3'>
          <div className='birthdays-page-icon'><FaBirthdayCake /></div>
          <div className='d-flex flex-column textPrimary'>
            <h2 className='m-0'>Aniversariantes</h2>
            <small className='text-muted'>Pacientes organizados por mês de aniversário</small>
          </div>
        </div>

        <div className='d-flex flex-column flex-md-row gap-2 col-12 col-md-auto'>
          <Button variant='outline-secondary' className='d-flex align-items-center gap-2 justify-content-center' onClick={() => setShowWhatsapp(true)}>
            <FaWhatsapp /> Mensagem do WhatsApp
          </Button>
          <Button variant='outline-secondary' className='d-flex align-items-center gap-2 justify-content-center' onClick={() => setShowGreetings(true)}>
            <IoMailOutline /> E-mail de aniversário
          </Button>
        </div>
      </div>

      <div className='px-3 pt-3 d-flex flex-column gap-3'>
        <div className='birthdays-months' role='tablist' aria-label='Meses'>
          {MONTHS.map((item) => (
            <button
              key={item.value}
              type='button'
              role='tab'
              aria-selected={item.value === month}
              title={item.full}
              className={`birthdays-month ${item.value === month ? 'active' : ''}`}
              onClick={() => setMonth(item.value)}
            >
              <span>{item.short}</span>
              <span className='birthdays-month-count'>{totals ? totals.find((total) => total.month === item.value)?.total ?? 0 : '…'}</span>
            </button>
          ))}
        </div>

        <div className='d-flex flex-column flex-sm-row gap-2'>
          <InputGroup className='birthdays-search'>
            <InputGroup.Text><FaSearch size={13} /></InputGroup.Text>
            <Form.Control
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder='Buscar por nome...'
              aria-label='Buscar por nome'
            />
          </InputGroup>

          <InputGroup className='birthdays-display'>
            <InputGroup.Text>Exibir</InputGroup.Text>
            <Form.Select
              value={display}
              onChange={(e) => setDisplayByMonth((current) => ({ ...current, [month]: e.target.value }))}
              aria-label='Exibir'
            >
              {BIRTHDAY_DISPLAYS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </Form.Select>
          </InputGroup>
        </div>

        {loading ?
          <div className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 row-cols-xxl-5 g-2'>
            {Array.from({ length: 10 }).map((_, index) => (
              <div className='col' key={index}>
                <Card className='birthdays-card'>
                  <Card.Body className='p-2 d-flex gap-2'>
                    <Placeholder as='div' animation='glow'><Placeholder className='birthdays-avatar' /></Placeholder>
                    <Placeholder as='div' animation='glow' className='flex-grow-1'>
                      <Placeholder xs={9} /> <Placeholder xs={5} size='sm' /> <Placeholder xs={7} size='sm' />
                    </Placeholder>
                  </Card.Body>
                </Card>
              </div>
            ))}
          </div>
          : items.length === 0 ?
            <Card className='birthdays-card'>
              <Card.Body className='py-5 text-center text-muted'>
                {nome ? 'Nenhum paciente encontrado para a busca.' : BIRTHDAY_DISPLAYS.find((item) => item.value === display)?.empty}
              </Card.Body>
            </Card>
            :
            <>
              <div className='row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 row-cols-xxl-5 g-2'>
                {items.map((paciente) => (
                  <div className='col' key={paciente.id}>
                    <BirthdayCard paciente={paciente} whatsappMessage={whatsappMessage} onEdit={handleEdit} />
                  </div>
                ))}
              </div>

              <div ref={loaderRef} className='d-flex justify-content-center py-2'>
                <small className='text-muted fst-italic'>
                  {loadingMore ? 'Carregando mais...' : hasNextPage ? 'Role para carregar mais' : `${paging.total_records} ${paging.total_records === 1 ? 'paciente' : 'pacientes'}`}
                </small>
              </div>
            </>
        }
      </div>

      <BirthdayGreetingsModal
        show={showGreetings}
        onHide={() => setShowGreetings(false)}
        defaultTestEmail={user?.email || ''}
        onChanged={() => loadPage(1)}
      />

      <BirthdayWhatsappModal
        show={showWhatsapp}
        onHide={() => setShowWhatsapp(false)}
        onSaved={(message) => setWhatsappMessage(message || DEFAULT_WHATSAPP_MESSAGE)}
      />
    </div>
  )
}

export default BirthdaysPage
